'use client';

import { useSupabase } from "@/components/auth/auth-provider";
import { LinkWalletButton } from "@/components/auth/LinkWalletButton";
import { usePlayerAccount } from "@/hooks/use-player-account";
import { Button } from "../ui/button";
import { useWallet } from "@solana/wallet-adapter-react";
import { useWalletModal } from "@solana/wallet-adapter-react-ui";
import { useEffect } from "react";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { AuthService } from "@/services/AuthService";

interface AccountModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function AccountModal({ isOpen, onClose }: AccountModalProps) {
    const { session: session, supabase: supabase, web3Profile: web3Profile, isWeb3User: isWeb3User } = useSupabase();
    const { data: playerAccount, refetch } = usePlayerAccount();
    const { publicKey, disconnect } = useWallet();
    const { setVisible } = useWalletModal();
    const isMobile = useMediaQuery('(max-width: 768px)')

    // Quiet modal open logging
    useEffect(() => {}, [isOpen, session, isWeb3User])

    // When the modal opens, refetch the player data to ensure it's up to date.
    useEffect(() => {
        if (isOpen) {
            refetch();
        }
    }, [isOpen, refetch]);

    const handleLogout = async () => {
        if (session) {
            await AuthService.signOut();
        } else if (isWeb3User) {
            await disconnect();
        }

        onClose(); // Close the modal

        // Use a small delay to ensure wallet disconnection completes
        setTimeout(() => {
            // Force a full page reload to clear all state and redirect to home.
            window.location.href = '/';
        }, 100);
    }

    const handleConnectWalletClick = () => {
        // Close the account modal first
        onClose();
        // Use a timeout to prevent modal-on-modal conflicts
        setTimeout(() => {
            setVisible(true);
        }, 150)
    }

    // This is the true, persisted state from the database.
    const hasWalletLinked = !!playerAccount?.wallet_address;
    // This is the temporary state from the wallet adapter.
    const isWalletConnected = !!publicKey;

    // Guard: Only show modal if user is authenticated (either session OR Web3) and modal is open
    if ((!session && !isWeb3User) || !isOpen) {
        return null;
    }

    // Quiet render log

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-[10000] flex items-center justify-center font-retro">
            <div
                className={cn(
                    "ui-panel pointer-events-auto",
                    isMobile
                        ? "w-full h-full m-0 rounded-none flex flex-col"
                        : "w-96 max-w-[90vw] mx-4"
                )}
            >
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg text-yellow-400">Account</h2>
                    <button
                        onClick={onClose}
                        className="text-lg leading-none p-1 hover:text-red-500 transition-colors"
                    >
                        &times;
                    </button>
                </div>

                {/* Content */}
                <div className={cn("space-y-6", isMobile ? "grow overflow-y-auto" : "")}>
                    <div className="space-y-4">
                        {/* Email Section - only show for session users */}
                        {session && (
                            <div className="border border-stone-600 rounded-md p-3 bg-black/20">
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-stone-300">Email</p>
                                        <p className="text-white text-sm mt-1">{session.user.email}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Wallet Section */}
                        <div className="border border-stone-600 rounded-md p-3 bg-black/20">
                            <div className="space-y-3">
                                <div>
                                    <p className="text-sm font-medium text-stone-300">
                                        {isWeb3User ? 'Wallet Address' : 'Linked Wallet'}
                                    </p>
                                    <p className="text-white text-xs mt-1 break-all">
                                        {isWeb3User ? web3Profile?.wallet_address :
                                         hasWalletLinked ? playerAccount.wallet_address : 'No wallet linked.'}
                                    </p>
                                </div>

                                {/* Show the correct button based on state - only for session users */}
                                {session && !hasWalletLinked && !isWalletConnected && (
                                    <Button
                                        onClick={handleConnectWalletClick}
                                        className="w-full bg-stone-700 hover:bg-stone-600 border border-stone-600"
                                    >
                                        Connect Wallet
                                    </Button>
                                )}
                                {session && !hasWalletLinked && isWalletConnected && (
                                    <div className="w-full">
                                        <LinkWalletButton />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Logout/Disconnect Button */}
                    <div className={cn("border-t border-stone-600 pt-4", isMobile ? "mt-auto" : "")}>
                        <Button
                            variant="outline"
                            className="w-full bg-transparent border-stone-600 text-red-400 hover:bg-red-900/20 hover:border-red-500"
                            onClick={handleLogout}
                        >
                            {isWeb3User ? 'Disconnect Wallet' : 'Logout'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}